/*
 * Reading a slot from a component, and the one rule every reading obeys. One hook instead of
 * `useShell().registry.slots(k)` at a dozen call sites, and one place that decides two things: that
 * array slots come back in `modules.ts` order (the registry memoises the merge, so this is free), and
 * whose contributions a frame draws at all.
 *
 * That second rule is a product rule, not a frame's: the frontend carries every module it was built
 * with, and a module's doors, rows and bindings belong on screen only while the reader may use the
 * module. The instance has to run it (`[modules] enabled`, `Model.enabledModules`) — a door into a
 * module that is not running leads to routes that answer 404 — and the module has to admit this
 * reader (`FrontendModule.admits`: AI chats may be narrowed to one group's team members). The rail
 * asks the same question of its places (`core/shell/Rail.tsx`), through `moduleActive` below.
 */

import { useModel, useShell } from "../shell/context.tsx"
import type { Model } from "../store/model.ts"
import type { ModuleId, ModuleRegistry, Slots } from "./registry.ts"

/**
 * The slots that serve whoever administers a module rather than whoever uses it: an administrator
 * keeps Administration's tab of a module, its part of *Open to AI chats* and the names of its work in
 * the queue whether or not the module admits them as a reader — running the instance is not using AI
 * chats.
 */
const ADMINISTRATION: ReadonlySet<keyof Slots> = new Set<keyof Slots>([
	"adminPages",
	"agentReach",
	"workSubjects",
])

/**
 * Whether this reader may use module `id` right now: the instance runs it, and — unless the question
 * is about administering it — the module admits them.
 *
 * Each half answers yes until the server has said otherwise: the list of running modules while the
 * bootstrap has named none, and `admits` while the bootstrap has not said who the reader is. A client
 * that has not been told must not take a door away, and a door that appears and then vanishes on the
 * first catch-up is worse than one that stays. A module this build does not register admits everyone:
 * whether it runs is all there is to know about it.
 */
export function moduleActive(
	registry: ModuleRegistry,
	id: ModuleId,
	model: Model,
	administering = false,
): boolean {
	const running = model.enabledModules
	if (running !== null && running.length > 0 && !running.includes(id)) return false
	if (administering || model.me === null) return true
	const mod = registry.all().find((candidate) => candidate.id === id)
	return mod?.admits === undefined || mod.admits(model)
}

/**
 * The registered modules this reader may use right now, joined — a primitive, so a component
 * repaints when the answer changes and not whenever the model does.
 */
function activeModules(registry: ModuleRegistry, model: Model, administering: boolean): string {
	return registry
		.all()
		.filter((mod) => moduleActive(registry, mod.id, model, administering))
		.map((mod) => mod.id)
		.join(",")
}

/**
 * The contributions to one slot of the modules this reader may use right now, in the order
 * modules.ts lists them. The registry memoises each set it is asked for, so the answer keeps its
 * identity from one render to the next.
 */
export function useSlot<K extends keyof Slots>(slot: K): Slots[K] {
	const { registry } = useShell()
	const administering = ADMINISTRATION.has(slot)
	const active = useModel((model) => activeModules(registry, model, administering))
	return registry.slots(slot, active === "" ? [] : (active.split(",") as ModuleId[]))
}
